import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Calendar } from 'lucide-react';
import { useScrollPosition } from '../../hooks/useScrollPosition';
import { useTheme } from '../../hooks/useTheme';
import { ThemeToggle, Button } from '../ui';
import { cn } from '../../utils/cn';

export interface NavbarProps {
  onOpenConsultation: () => void;
}

export const Navbar: React.FC<NavbarProps> = ({ onOpenConsultation }) => {
  const { isScrolled } = useScrollPosition();
  const { resolvedTheme } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const isHomePage = location.pathname === '/';
  const isDark = resolvedTheme === 'dark';

  // Transparent header applies ONLY on Home hero in Light Mode
  const isTransparentOnBlue = !isScrolled && isHomePage && !isDark;
  
  // Header has a white background ONLY in Light Mode when scrolled or on subpages
  const isWhiteHeader = !isTransparentOnBlue && !isDark;

  // Logo should be inverted to white ONLY when NOT on a white header
  const shouldInvertLogo = !isWhiteHeader;

  const navLinks = [
    { label: 'Home', path: '/', hash: '#home' },
    { label: 'Services', path: '/services', hash: '#services' },
    { label: 'Why Us', path: '/', hash: '#why-us' },
    { label: 'About', path: '/about', hash: '#about' },
    { label: 'Contact', path: '/contact', hash: '#contact' },
  ];

  const handleNavClick = (e: React.MouseEvent, link: typeof navLinks[0]) => {
    if (isHomePage && link.hash) {
      e.preventDefault();
      const targetElement = document.querySelector(link.hash);
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth' });
      } else {
        navigate(link.path);
      }
    } else {
      navigate(link.path);
    }
    setMobileMenuOpen(false);
  };

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-40 transition-all duration-300',
        isWhiteHeader
          ? 'bg-white/95 backdrop-blur-xl border-b border-slate-200/80 shadow-lg py-3.5'
          : isTransparentOnBlue
          ? 'bg-transparent py-5'
          : 'bg-[#080e1a]/95 backdrop-blur-xl border-b border-slate-800/80 shadow-2xl py-3.5'
      )}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
        
        {/* Left: Official Technook Brand Lockup */}
        <Link to="/" className="flex items-center gap-3 group">
          <img
            src="/assets/technook/technook-logo-320.png"
            alt="TechNook Global Logo"
            className={cn(
              'h-9 w-auto object-contain transition-transform duration-300 group-hover:scale-105',
              shouldInvertLogo && 'brightness-0 invert'
            )}
          />
          <div className="flex flex-col">
            <span className={cn(
              'text-xl font-black tracking-tight leading-none transition-colors',
              isWhiteHeader ? 'text-[#184166]' : 'text-white'
            )}>
              Tech<span className={isWhiteHeader ? 'text-[#4a82c2]' : 'text-sky-300'}>Nook</span>
            </span>
            <span className={cn(
              'text-[10px] font-bold tracking-widest uppercase mt-1 transition-colors',
              isWhiteHeader ? 'text-slate-600' : 'text-sky-100 dark:text-slate-400'
            )}>
              Global Solutions
            </span>
          </div>
        </Link>

        {/* Center: Modern Sleek Navigation Menu */}
        <nav className="hidden md:flex items-center space-x-1 lg:space-x-2">
          {navLinks.map((link) => {
            const isActive = location.pathname === link.path;
            return (
              <a
                key={link.label}
                href={link.hash}
                onClick={(e) => handleNavClick(e, link)}
                className={cn(
                  'px-3.5 py-2 text-xs font-bold tracking-wide rounded-xl transition-all cursor-pointer',
                  isTransparentOnBlue
                    ? 'text-white/90 hover:text-white hover:bg-white/15'
                    : isWhiteHeader
                    ? 'text-slate-700 hover:text-[#4a82c2] hover:bg-sky-50'
                    : 'text-slate-300 hover:text-white hover:bg-[#4a82c2]/20',
                  isActive && isWhiteHeader && 'text-[#4a82c2] font-extrabold',
                  isActive && !isWhiteHeader && 'text-sky-300 font-extrabold'
                )}
              >
                {link.label}
              </a>
            );
          })}
        </nav>

        {/* Right: Action Hub with Theme Switcher & CTA */}
        <div className="hidden md:flex items-center gap-3">
          <ThemeToggle />

          <Button variant="primary" size="sm" onClick={onOpenConsultation} className="shadow-lg shadow-[#4a82c2]/20">
            <Calendar size={14} />
            <span>Book Appointment</span>
          </Button>
        </div>

        {/* Mobile Hamburger Toggle */}
        <div className="flex items-center gap-2 md:hidden">
          <ThemeToggle />
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 text-slate-700 dark:text-slate-300 hover:text-[#4a82c2] dark:hover:text-white rounded-xl bg-white dark:bg-[#0f1b2d] border border-slate-200 dark:border-slate-800 shadow-sm"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

      </div>

      {/* Mobile Drawer */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white/95 dark:bg-[#080e1a]/95 border-b border-slate-200 dark:border-slate-800 backdrop-blur-2xl px-4 pt-4 pb-6 space-y-3 animate-in slide-in-from-top duration-200 shadow-xl">
          <div className="flex flex-col space-y-1">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.hash}
                onClick={(e) => handleNavClick(e, link)}
                className="px-4 py-2.5 text-sm font-semibold text-slate-800 dark:text-slate-200 hover:bg-sky-50 dark:hover:bg-[#0f1b2d] rounded-xl cursor-pointer"
              >
                {link.label}
              </a>
            ))}
          </div>
          <div className="pt-3 border-t border-slate-200 dark:border-slate-800 flex flex-col gap-2">
            <Button variant="primary" size="md" className="w-full" onClick={() => { setMobileMenuOpen(false); onOpenConsultation(); }}>
              <Calendar size={16} />
              <span>Book Appointment</span>
            </Button>
          </div>
        </div>
      )}
    </header>
  );
};
